import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { api } from '../services/api.ts'
import LoadingSpinner from '../components/LoadingSpinner.tsx'
import CostEstimateModal from '../components/CostEstimateModal.tsx'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'

interface JobFormData {
  state: string
  keywords: string[]
  filters: {
    radius: number
  }
  enable_yelp: boolean // NEW
  enable_ai_filtering: boolean // NEW
}

interface CostEstimate {
  state: string
  keywords: string[]
  city_count: number
  consolidated_keywords?: number
  keyword_count?: number
  method?: string
  estimates: {
    optimistic: {
      total: number
      breakdown: {
        text_search?: { calls: number; cost: number }
        nearby_search?: { calls: number; cost: number }
        place_details: { calls: number; cost: number }
      }
      assumptions: {
        total_results?: number
        unique_places?: number
        new_places?: number
        total_stores_google?: number
        total_stores_with_osm?: number
        unique_stores?: number
        new_stores?: number
        cache_hit_rate: number
        duplicate_rate: number
        avg_pages_per_search?: number
        osm_contribution?: number
      }
    }
    realistic: {
      total: number
      breakdown: {
        text_search?: { calls: number; cost: number }
        nearby_search?: { calls: number; cost: number }
        place_details: { calls: number; cost: number }
      }
      assumptions: {
        total_results?: number
        unique_places?: number
        new_places?: number
        total_stores_google?: number
        total_stores_with_osm?: number
        unique_stores?: number
        new_stores?: number
        cache_hit_rate: number
        duplicate_rate: number
        avg_pages_per_search?: number
        osm_contribution?: number
      }
    }
    pessimistic: {
      total: number
      breakdown: {
        text_search?: { calls: number; cost: number }
        nearby_search?: { calls: number; cost: number }
        place_details: { calls: number; cost: number }
      }
      assumptions: {
        total_results?: number
        unique_places?: number
        new_places?: number
        total_stores_google?: number
        total_stores_with_osm?: number
        unique_stores?: number
        new_stores?: number
        cache_hit_rate: number
        duplicate_rate: number
        avg_pages_per_search?: number
        osm_contribution?: number
      }
    }
  }
  assumptions: {
    text_search_cost_per_1000?: number
    nearby_search_cost_per_1000?: number
    place_details_cost_per_1000: number
    note: string
  }
}

export default function CreateJob() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1)
  const [showCostModal, setShowCostModal] = useState(false)
  const [costEstimate, setCostEstimate] = useState<CostEstimate | null>(null)
  const [isEstimatingCost, setIsEstimatingCost] = useState(false)

  // Fallback states list in case API fails
  const fallbackStates = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ]

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<JobFormData>({
    defaultValues: {
      state: '',
      keywords: [
        'vape',
        'vape shop',
        'vape wholesale',
        'vape distributor',
        'smoke shop',
        'e-juice',
        'vape retailer',
        'disposable retailer',
        'vapor wholesale',
        'smoke retailer',
        'smoke wholesale',
        'disposable vape',
        'e-cig',
        'Electronic Cigarettes',
      ],
      filters: {
        radius: 50000,
      },
      enable_yelp: false,
      enable_ai_filtering: true,
    },
  })

  const { data: config } = useQuery('config', () =>
    api.get('/api/settings/config/defaults')
  )

  // Update form keywords when config loads
  useEffect(() => {
    if (config?.data?.default_keywords) {
      setValue('keywords', config.data.default_keywords)
    }
  }, [config, setValue])

  // Cost estimation mutation
  const estimateCostMutation = useMutation(
    async (data: { state: string; keywords: string[] }) => {
      const response = await api.post('/api/jobs/estimate-cost', {
        state: data.state,
        keywords: data.keywords,
      })
      return response.data
    },
    {
      onSuccess: (data) => {
        setCostEstimate(data)
        setIsEstimatingCost(false)
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to estimate cost')
        setIsEstimatingCost(false)
      },
    }
  )

  const createJobMutation = useMutation(
    (data: JobFormData) => api.post('/api/jobs/', data),
    {
      onSuccess: (response) => {
        toast.success('Job created successfully')
        queryClient.invalidateQueries('jobs')
        queryClient.invalidateQueries('all-jobs')
        navigate(`/jobs/${response.data.id}`)
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to create job')
      },
    }
  )

  const onSubmit = (data: JobFormData) => {
    // Validate that all keywords are filled
    const validKeywords = data.keywords.filter(
      (keyword) => keyword.trim() !== ''
    )
    if (validKeywords.length === 0) {
      toast.error('Please enter at least one keyword')
      return
    }

    createJobMutation.mutate(data)
  }

  const addKeyword = () => {
    const currentKeywords = watch('keywords') || []
    setValue('keywords', [...currentKeywords, ''])
  }

  const removeKeyword = (index: number) => {
    const currentKeywords = watch('keywords') || []
    setValue(
      'keywords',
      currentKeywords.filter((_, i) => i !== index)
    )
  }

  const updateKeyword = (index: number, value: string) => {
    const currentKeywords = watch('keywords') || []
    const newKeywords = [...currentKeywords]
    newKeywords[index] = value
    setValue('keywords', newKeywords)
  }

  const handleNextStep = () => {
    const formData = watch()

    // Validate step 1
    if (currentStep === 1) {
      if (!formData.state) {
        toast.error('Please select a state')
        return
      }
      const validKeywords = formData.keywords.filter((k) => k.trim() !== '')
      if (validKeywords.length === 0) {
        toast.error('Please enter at least one keyword')
        return
      }

      // Estimate cost before proceeding
      setIsEstimatingCost(true)
      estimateCostMutation.mutate({
        state: formData.state,
        keywords: validKeywords,
      })
      setShowCostModal(true)
    }
  }

  const handleCostModalProceed = () => {
    setShowCostModal(false)
    setCurrentStep(2)
  }

  const handleCostModalCancel = () => {
    setShowCostModal(false)
    setIsEstimatingCost(false)
  }

  const handleBackStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleFinalSubmit = () => {
    const formData = watch()
    onSubmit(formData)
  }

  const steps = [
    { id: 1, name: 'Configure Job', description: 'Select state and keywords' },
    { id: 2, name: 'Review Cost', description: 'Review cost estimate' },
    { id: 3, name: 'Confirm', description: 'Create the job' },
  ]

  return (
    <div className='max-w-4xl mx-auto'>
      <div className='space-y-6'>
        {/* Header */}
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
            Create New Job
          </h1>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Configure a new scraping job for a specific state
          </p>
        </div>

        {/* Step Progress */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg'>
          <div className='px-6 py-4'>
            <nav aria-label='Progress'>
              <ol className='flex items-center justify-between'>
                {steps.map((step, stepIdx) => (
                  <li
                    key={step.id}
                    className={`${
                      stepIdx !== steps.length - 1 ? 'pr-8 sm:pr-20' : ''
                    } relative`}
                  >
                    <div className='flex items-center'>
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          currentStep > step.id
                            ? 'bg-green-600 text-white'
                            : currentStep === step.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {currentStep > step.id ? (
                          <CheckIcon className='w-5 h-5' />
                        ) : (
                          <span className='text-sm font-medium'>{step.id}</span>
                        )}
                      </div>
                      <div className='ml-4 min-w-0'>
                        <p
                          className={`text-sm font-medium ${
                            currentStep >= step.id
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {step.name}
                        </p>
                        <p className='text-xs text-gray-500 dark:text-gray-400'>
                          {step.description}
                        </p>
                      </div>
                    </div>
                    {stepIdx !== steps.length - 1 && (
                      <div className='absolute top-4 left-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-200 dark:bg-gray-700' />
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </div>

        {/* Step Content */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg'>
          <div className='px-6 py-6'>
            {currentStep === 1 && (
              <div className='space-y-6'>
                <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
                  Job Configuration
                </h3>

                {/* State Selection */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                    State
                  </label>
                  <select
                    {...register('state', { required: 'State is required' })}
                    className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                  >
                    <option value=''>Select a state</option>
                    {fallbackStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <p className='mt-1 text-sm text-red-600 dark:text-red-400'>
                      {errors.state.message}
                    </p>
                  )}
                </div>

                {/* Keywords */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                    Keywords
                  </label>
                  <div className='space-y-2'>
                    {watch('keywords')?.map((keyword, index) => (
                      <div key={index} className='flex items-center space-x-2'>
                        <input
                          type='text'
                          value={keyword}
                          onChange={(e) => updateKeyword(index, e.target.value)}
                          className='flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                          placeholder={`Keyword ${index + 1}`}
                        />
                        <button
                          type='button'
                          onClick={() => removeKeyword(index)}
                          className='px-3 py-2 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type='button'
                    onClick={addKeyword}
                    className='mt-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                  >
                    + Add Keyword
                  </button>
                </div>

                {/* Radius */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                    Search Radius (meters)
                  </label>
                  <input
                    type='number'
                    {...register('filters.radius', {
                      required: 'Radius is required',
                      min: {
                        value: 1000,
                        message: 'Minimum radius is 1000 meters',
                      },
                      max: {
                        value: 100000,
                        message: 'Maximum radius is 100000 meters',
                      },
                    })}
                    className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
                  />
                  {errors.filters?.radius && (
                    <p className='mt-1 text-sm text-red-600 dark:text-red-400'>
                      {errors.filters.radius.message}
                    </p>
                  )}
                </div>

                {/* Data Sources Configuration */}
                <div className='space-y-4'>
                  <h4 className='text-md font-medium text-gray-900 dark:text-gray-300'>
                    Data Sources
                  </h4>

                  {/* Google Places - always enabled, show as info */}
                  <div className='flex items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg'>
                    <CheckIcon className='h-5 w-5 text-green-500 mr-3' />
                    <div>
                      <span className='text-sm font-medium text-green-900 dark:text-green-200'>
                        Google Places API (Primary Source)
                      </span>
                      <p className='text-xs text-green-700 dark:text-green-300'>
                        Always enabled - provides the most comprehensive data
                      </p>
                    </div>
                  </div>

                  {/* OpenStreetMap - always enabled, show as info */}
                  <div className='flex items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg'>
                    <CheckIcon className='h-5 w-5 text-blue-500 mr-3' />
                    <div>
                      <span className='text-sm font-medium text-blue-900 dark:text-blue-200'>
                        OpenStreetMap (Supplemental, Free)
                      </span>
                      <p className='text-xs text-blue-700 dark:text-blue-300'>
                        Always enabled - community-driven data, no API costs
                      </p>
                    </div>
                  </div>

                  {/* Yelp - optional toggle */}
                  <div className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg'>
                    <label className='flex items-center cursor-pointer'>
                      <input
                        type='checkbox'
                        {...register('enable_yelp')}
                        className='form-checkbox h-5 w-5 text-blue-600 rounded'
                      />
                      <div className='ml-3'>
                        <span className='text-sm font-medium text-gray-900 dark:text-gray-200'>
                          Yelp Fusion API (Optional)
                        </span>
                        <p className='text-xs text-gray-600 dark:text-gray-400'>
                          5000 calls/day free - recommended for better coverage
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* AI Filtering Configuration */}
                <div className='space-y-4'>
                  <h4 className='text-md font-medium text-gray-900 dark:text-gray-300'>
                    AI-Powered Filtering
                  </h4>

                  <div className='flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg'>
                    <label className='flex items-center cursor-pointer'>
                      <input
                        type='checkbox'
                        {...register('enable_ai_filtering')}
                        defaultChecked={true}
                        className='form-checkbox h-5 w-5 text-purple-600 rounded'
                      />
                      <div className='ml-3'>
                        <span className='text-sm font-medium text-purple-900 dark:text-purple-200'>
                          AI-Powered Filtering (Recommended)
                        </span>
                        <p className='text-xs text-purple-700 dark:text-purple-300'>
                          Removes non-vape businesses using Claude Haiku -
                          ~$0.05 per 1000 businesses
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && costEstimate && (
              <div className='space-y-6'>
                <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
                  Cost Estimate Review
                </h3>

                <div className='bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4'>
                  <h4 className='font-medium text-blue-900 dark:text-blue-200 mb-2'>
                    Estimated Cost: $
                    {costEstimate.estimates.realistic.total.toFixed(2)}
                  </h4>
                  <p className='text-sm text-blue-800 dark:text-blue-300'>
                    This is the realistic estimate based on historical data.
                    Actual costs may vary between $
                    {costEstimate.estimates.optimistic.total.toFixed(2)}
                    and ${costEstimate.estimates.pessimistic.total.toFixed(2)}.
                  </p>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div className='bg-green-50 dark:bg-green-900/20 rounded-lg p-4'>
                    <h5 className='font-medium text-green-900 dark:text-green-200'>
                      Optimistic
                    </h5>
                    <p className='text-2xl font-bold text-green-600 dark:text-green-400'>
                      ${costEstimate.estimates.optimistic.total.toFixed(2)}
                    </p>
                    <p className='text-sm text-green-700 dark:text-green-300'>
                      {costEstimate.estimates.optimistic.breakdown.text_search?.calls ||
                        costEstimate.estimates.optimistic.breakdown.nearby_search?.calls || 0}{' '}
                      searches,{' '}
                      {costEstimate.estimates.optimistic.breakdown.place_details.calls}{' '}
                      details
                    </p>
                  </div>

                  <div className='bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4'>
                    <h5 className='font-medium text-blue-900 dark:text-blue-200'>
                      Realistic
                    </h5>
                    <p className='text-2xl font-bold text-blue-600 dark:text-blue-400'>
                      ${costEstimate.estimates.realistic.total.toFixed(2)}
                    </p>
                    <p className='text-sm text-blue-700 dark:text-blue-300'>
                      {costEstimate.estimates.realistic.breakdown.text_search?.calls ||
                        costEstimate.estimates.realistic.breakdown.nearby_search?.calls || 0}{' '}
                      searches,{' '}
                      {costEstimate.estimates.realistic.breakdown.place_details.calls}{' '}
                      details
                    </p>
                  </div>

                  <div className='bg-red-50 dark:bg-red-900/20 rounded-lg p-4'>
                    <h5 className='font-medium text-red-900 dark:text-red-200'>
                      Pessimistic
                    </h5>
                    <p className='text-2xl font-bold text-red-600 dark:text-red-400'>
                      ${costEstimate.estimates.pessimistic.total.toFixed(2)}
                    </p>
                    <p className='text-sm text-red-700 dark:text-red-300'>
                      {costEstimate.estimates.pessimistic.breakdown.text_search?.calls ||
                        costEstimate.estimates.pessimistic.breakdown.nearby_search?.calls || 0}{' '}
                      searches,{' '}
                      {costEstimate.estimates.pessimistic.breakdown.place_details.calls}{' '}
                      details
                    </p>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className='space-y-6'>
                <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
                  Confirm Job Creation
                </h3>

                <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4'>
                  <h4 className='font-medium text-gray-900 dark:text-white mb-2'>
                    Job Summary
                  </h4>
                  <div className='grid grid-cols-2 gap-4 text-sm'>
                    <div>
                      <span className='text-gray-500 dark:text-gray-400'>
                        State:
                      </span>
                      <span className='ml-2 font-medium text-gray-900 dark:text-white'>
                        {watch('state')}
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500 dark:text-gray-400'>
                        Keywords:
                      </span>
                      <span className='ml-2 font-medium text-gray-900 dark:text-white'>
                        {
                          watch('keywords')?.filter((k) => k.trim() !== '')
                            .length
                        }
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500 dark:text-gray-400'>
                        Radius:
                      </span>
                      <span className='ml-2 font-medium text-gray-900 dark:text-white'>
                        {watch('filters.radius')?.toLocaleString()} meters
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500 dark:text-gray-400'>
                        Estimated Cost:
                      </span>
                      <span className='ml-2 font-medium text-gray-900 dark:text-white'>
                        $
                        {costEstimate?.estimates.realistic.total.toFixed(2) ||
                          'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className='bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4'>
                  <p className='text-sm text-yellow-800 dark:text-yellow-300'>
                    <strong>Note:</strong> Once created, this job will start
                    running immediately and will consume Google Places API
                    credits. Make sure you're satisfied with the configuration
                    before proceeding.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Step Navigation */}
          <div className='flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700'>
            <button
              type='button'
              onClick={handleBackStep}
              disabled={currentStep === 1}
              className='flex items-center justify-center h-10 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <ChevronLeftIcon className='w-4 h-4 mr-2' />
              Back
            </button>

            <div className='flex items-center space-x-3'>
              {currentStep === 1 && (
                <button
                  type='button'
                  onClick={handleNextStep}
                  disabled={isEstimatingCost}
                  className='flex items-center justify-center h-10 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isEstimatingCost ? (
                    <>
                      <LoadingSpinner />
                      <span className='ml-2'>Estimating...</span>
                    </>
                  ) : (
                    <>
                      <span>Next</span>
                      <ChevronRightIcon className='w-4 h-4 ml-2' />
                    </>
                  )}
                </button>
              )}

              {currentStep === 2 && (
                <button
                  type='button'
                  onClick={() => setCurrentStep(3)}
                  className='flex items-center justify-center h-10 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                >
                  <span>Next</span>
                  <ChevronRightIcon className='w-4 h-4 ml-2' />
                </button>
              )}

              {currentStep === 3 && (
                <button
                  type='button'
                  onClick={handleFinalSubmit}
                  disabled={createJobMutation.isLoading}
                  className='flex items-center justify-center h-10 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {createJobMutation.isLoading ? (
                    <>
                      <LoadingSpinner />
                      <span className='ml-2'>Creating...</span>
                    </>
                  ) : (
                    <>
                      <CheckIcon className='w-4 h-4 mr-2' />
                      <span>Create Job</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Cost Estimate Modal */}
        <CostEstimateModal
          isOpen={showCostModal}
          onClose={handleCostModalCancel}
          onProceed={handleCostModalProceed}
          estimate={costEstimate}
          isLoading={isEstimatingCost}
        />
      </div>
    </div>
  )
}
